import { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { getMainHero } from './functions'
import { getAllHeroesFromWallets } from '../../services/hero.service'

import Alert from '@mui/material/Alert'
import ConnectWallet from '../../containers/ConnectWallet'
import HeroList from './HeroList'
import LookupByIdForm from './LookupByIdForm'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import WalletForm from './WalletForm'

import './styles.css'

const TabPanel = ({ active, children, id }) => {
    return id === active ? <>{children}</> : null
}

const HeroLookup = ({ name, onHeroSelected }) => {
    const [error, setError] = useState('')
    const [heroList, setHeroList] = useState([])
    const [loadingHeroes, setLoadingHeroes] = useState(false)
    const [lookingUpHero, setLookingUpHero] = useState(false)
    const [selectedHero, setSelectedHero] = useState(null)
    const [tab, setTab] = useState('id')

    // Change the selected tab
    const handleTabChange = (event, value) => {
        setTab(value)
    }

    // Return the selected hero
    const handleHeroSelected = hero => {
        setSelectedHero(hero)
        onHeroSelected({ target: { name: name, value: hero } })
    }

    // Load all heroes from specified wallets
    const loadHeroesFromWallets = useCallback(async walletAddresses => {
        setLoadingHeroes(true)
        setSelectedHero(null)

        let walletHeroes = await getAllHeroesFromWallets(walletAddresses)

        console.log(`${walletHeroes.length} heroes loaded from wallet ${walletAddresses.join(', ')}`)

        setHeroList(walletHeroes)

        setLoadingHeroes(false)
    }, [])

    // Looks up the Hero for the given id
    // Note that will return heroes from all 3 networks if they exist: Serendale, CrystalVale, Serendale 2.0
    const loadHeroById = async ({ heroId }) => {
        if (heroId && (!selectedHero || selectedHero.id !== heroId)) {
            // Clear currently displayed main and matching heroes
            setLookingUpHero(true)
            setSelectedHero()
            setError('')

            // Retrieve all heroes from DFK that match the ID
            const _heroes = await getMainHero(heroId)

            if (_heroes.length) {
                setHeroList(_heroes)
            } else {
                setError('Hero not found.')
            }

            setLookingUpHero(false)
        }
    }

    return (
        <div className="hero-lookup">
            <Tabs value={tab} onChange={handleTabChange}>
                <Tab label="By Hero Id" value="id" />
                <Tab label="From My Wallet" value="myWallet" />
                <Tab label="From Any Wallets" value="wallet" />
            </Tabs>

            <TabPanel active={tab} id="id">
                <div className="instructions">
                    You can look up a hero by entering their hero id. Once the desired hero is displayed, click the hero
                    card to select the hero and continue to the match criteria page.
                </div>
                <LookupByIdForm onLoadHero={loadHeroById} />
            </TabPanel>

            <TabPanel active={tab} id="wallet">
                <div className="instructions">
                    Enter the wallet address(es) and then click the magnifying glass to view a list of heroes to choose
                    from. Click the hero card to select the hero and continue to the <i>Match Criteria</i> page.
                </div>
                <WalletForm onSubmit={loadHeroesFromWallets} />
            </TabPanel>

            <TabPanel active={tab} id="myWallet">
                <div className="instructions">
                    When using the <i>From My Wallet</i> option, you must connect your wallet by giving the site
                    permission to access your wallet. Once your wallet is connected, your list of heroes will be loaded
                    for you to choose from. Click the hero card to select the hero and continue to the
                    <i>Match Criteria</i> page.
                    {/* <p />
                    This feature will also allow you to summon without leaving the site. You will be required to approve
                    all transactions.
                    <p />
                    !!! No transactions will be performed without your approval !!! */}
                </div>
                <ConnectWallet onConnected={loadHeroesFromWallets} />
            </TabPanel>

            {(error || lookingUpHero || loadingHeroes) && (
                <Alert variant="outlined" severity="success" className="loading-message">
                    {error && error}
                    {lookingUpHero && 'Looking up hero...'}
                    {loadingHeroes && 'Loading heroes...'}
                </Alert>
            )}

            <HeroList heroes={heroList} onHeroSelected={handleHeroSelected} />
        </div>
    )
}

HeroLookup.propTypes = {
    name: PropTypes.string,
    onHeroSelected: PropTypes.func,
}

HeroLookup.defaultProps = {
    onHeroSelected: () => {},
}

export default HeroLookup
